<template>
  <Teleport to="body">
    <Transition name="gs">
      <div v-if="isVisible" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/40" @click="close"></div>

        <div class="relative bg-white border border-zinc-100 rounded-2xl w-full max-w-xl overflow-hidden">
          <!-- Close -->
          <button
            @click="close"
            class="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 transition-colors"
            aria-label="Close"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>

          <!-- Header -->
          <div class="px-8 pt-8 pb-6 text-center">
            <p class="text-xs font-semibold uppercase tracking-widest text-gold mb-2">Get started</p>
            <h2 class="text-xl font-bold text-zinc-900 tracking-tight">How would you like to start?</h2>
          </div>

          <!-- Idle: two option cards -->
          <div v-if="state === 'idle'" class="px-6 pb-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Upload card -->
            <div
              @click="triggerUpload"
              class="border border-zinc-100 rounded-xl p-6 cursor-pointer hover:border-primary transition-colors group flex flex-col items-center text-center gap-3"
            >
              <div class="w-11 h-11 rounded-xl border border-zinc-100 group-hover:border-primary transition-colors flex items-center justify-center">
                <svg class="w-5 h-5 text-zinc-400 group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                </svg>
              </div>
              <div>
                <p class="text-sm font-semibold text-zinc-900 mb-1">Upload current CV</p>
                <p class="text-xs text-zinc-400 leading-relaxed">We'll extract all your details automatically</p>
              </div>
              <p class="text-xs text-zinc-300 mt-auto">PDF, DOCX, JPG, PNG</p>
            </div>

            <!-- Scratch card -->
            <div
              @click="startFromScratch"
              class="border border-zinc-100 rounded-xl p-6 cursor-pointer hover:border-primary transition-colors group flex flex-col items-center text-center gap-3"
            >
              <div class="w-11 h-11 rounded-xl border border-zinc-100 group-hover:border-primary transition-colors flex items-center justify-center">
                <svg class="w-5 h-5 text-zinc-400 group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                </svg>
              </div>
              <div>
                <p class="text-sm font-semibold text-zinc-900 mb-1">Start from scratch</p>
                <p class="text-xs text-zinc-400 leading-relaxed">Fill in your details in the editor</p>
              </div>
              <p class="text-xs text-zinc-300 mt-auto">Blank editor, full control</p>
            </div>
          </div>

          <!-- Uploading / parsing state -->
          <div v-else-if="state === 'uploading'" class="px-8 pb-10 flex flex-col items-center gap-4 text-center">
            <div class="w-11 h-11 rounded-xl border border-zinc-100 flex items-center justify-center">
              <svg class="w-5 h-5 text-zinc-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 5.373 0 12h4z"/>
              </svg>
            </div>
            <div>
              <p class="text-sm font-semibold text-zinc-900">Analyzing your CV…</p>
              <p class="text-xs text-zinc-400 mt-1">This usually takes 10–20 seconds</p>
            </div>
          </div>

          <!-- Error state -->
          <div v-else-if="state === 'error'" class="px-8 pb-8 flex flex-col items-center gap-4 text-center">
            <div class="w-11 h-11 rounded-xl border border-zinc-100 flex items-center justify-center">
              <svg class="w-5 h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              </svg>
            </div>
            <div>
              <p class="text-sm font-semibold text-zinc-900">Couldn't read your CV</p>
              <p class="text-xs text-zinc-400 mt-1 max-w-xs">{{ errorMessage }}</p>
            </div>
            <div class="flex gap-3">
              <button @click="retry" class="px-4 py-2 text-xs font-semibold text-white bg-primary rounded-lg hover:opacity-90 transition-opacity">
                Try again
              </button>
              <button @click="startFromScratch" class="px-4 py-2 text-xs font-semibold text-zinc-600 border border-zinc-200 rounded-lg hover:border-primary transition-colors">
                Start from scratch
              </button>
            </div>
          </div>
        </div>

        <!-- Hidden file input -->
        <input
          ref="fileInput"
          type="file"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
          class="hidden"
          @change="handleFileChange"
        />
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { validateCvParserFile, parseCvParserError } from '../config/cvConfig.js'

const props = defineProps({
  isVisible: { type: Boolean, default: false }
})
const emit = defineEmits(['close'])

const router = useRouter()
const fileInput = ref(null)
const state = ref('idle') // 'idle' | 'uploading' | 'error'
const errorMessage = ref('')

const close = () => {
  if (state.value === 'uploading') return
  state.value = 'idle'
  errorMessage.value = ''
  emit('close')
}

const startFromScratch = () => {
  localStorage.removeItem('cv-tool.com_autosave')
  emit('close')
  router.push('/cv-creator')
}

const triggerUpload = () => {
  fileInput.value?.click()
}

const retry = () => {
  state.value = 'idle'
  errorMessage.value = ''
  fileInput.value.value = ''
}

const handleFileChange = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  const fileError = validateCvParserFile(file)
  if (fileError) {
    state.value = 'error'
    errorMessage.value = fileError
    return
  }

  state.value = 'uploading'
  errorMessage.value = ''

  try {
    const formData = new FormData()
    formData.append('file', file)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const response = await fetch('/api/cv_parser/upload', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json' },
      body: formData
    })

    const data = await response.json()

    if (!response.ok || !data.success) {
      throw new Error(parseCvParserError(response, data))
    }

    const converted = convertParsedData(data.cv_data)
    sessionStorage.setItem('cv_parser_result', JSON.stringify({ data: converted, timestamp: Date.now() }))

    emit('close')
    router.push('/cv-creator')
  } catch (err) {
    state.value = 'error'
    errorMessage.value = err.message || 'Please try a different file or start from scratch.'
  }
}

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) return obj.map(toCamelCase)
  if (obj !== null && typeof obj === 'object') {
    const result = {}
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
      result[camelKey] = toCamelCase(value)
    }
    return result
  }
  return obj
}

const convertParsedData = (raw) => {
  const c = toCamelCase(raw)
  return {
    personalInfo: c.personalInfo || {},
    educationList: c.educationList || [],
    experienceList: c.experienceList || [],
    activitiesList: c.activitiesList || [],
    skillCategories: (c.skillCategoriesList || []).map(cat => ({
      name: cat.categoryName || cat.name || '',
      content: cat.content || ''
    }))
  }
}
</script>

<style scoped>
.gs-enter-active, .gs-leave-active { transition: opacity 0.15s ease; }
.gs-enter-from, .gs-leave-to { opacity: 0; }
</style>
